import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const TestimonialsSection = styled.section`
  background-color: white;
  padding: 100px 0;
  overflow: hidden;
`;

const TestimonialsContainer = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionSubtitle = styled(motion.span)`
  display: inline-block;
  font-size: 1rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 12px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: 30px;
    background-color: var(--secondary-color);
    transform: translateY(-50%);
  }
  
  &:before {
    right: calc(100% + 10px);
  }
  
  &:after {
    left: calc(100% + 10px);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled(motion.p)`
  font-size: 1.1rem;
  color: var(--text-muted);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
`;

const TestimonialsWrapper = styled.div`
  position: relative;
`;

const TestimonialSlider = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  transform: translateX(${({ currentSlide }) => currentSlide * -100}%);
`;

const TestimonialSlide = styled.div`
  min-width: 100%;
  display: flex;
  justify-content: center;
  gap: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const TestimonialCard = styled(motion.div)`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 40px;
  width: calc(50% - 15px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  position: relative;
  border: 1px solid var(--grey-100);
  
  &:before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 25px;
    font-size: 120px;
    line-height: 1;
    font-family: Georgia, serif;
    color: rgba(14, 116, 144, 0.1);
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 500px;
    margin-bottom: 20px;
  }
`;

const TestimonialContent = styled.div`
  position: relative;
  z-index: 1;
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  color: var(--text-muted);
  line-height: 1.7;
  margin-bottom: 30px;
  font-style: italic;
`;

const TestimonialClient = styled.div`
  display: flex;
  align-items: center;
`;

const ClientImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 20px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ClientInfo = styled.div`
  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 5px;
  }
  
  p {
    font-size: 0.9rem;
    color: var(--text-muted);
  }
`;

const TestimonialControls = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 50px;
  gap: 15px;
`;

const ControlButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? 'var(--primary-color)' : 'white')};
  border: 1px solid ${({ $active }) => ($active ? 'transparent' : 'var(--grey-200)')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-muted)')};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${({ $active }) => ($active ? 'var(--primary-color)' : 'var(--grey-100)')};
    transform: translateY(-3px);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const TestimonialDots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  gap: 10px;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? 'var(--primary-color)' : 'var(--grey-200)')};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${({ $active }) => ($active ? 'var(--primary-color)' : 'var(--grey-300)')};
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Testimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoSlide, setAutoSlide] = useState(true);
  
  const testimonials = [
    [
      {
        text: "Websigma has been managing our website in terms of updating on demand, availability and SEO as well as managing our search ads. The team has been doing a stellar job at supporting us and have been very responsive to our requests. We recommend them wholeheartedly",
        name: "Shekar Prabhakar",
        position: "CEO,Hasirudala",
        image: "https://hasirudalainnovations.com/img/about-us/shekar.jpeg"
      },
      {
        text: "It was great working with Websigma. Their approach of understanding our requirements and being supportive during our association was a good experience. I wish them the best.",
        name: "Dinesh Kotha",
        position: "CEO,Confirmtkt",
        image: "https://images.yourstory.com/cs/images/people/C7BNKT6W400x400-1596722008878.jpg?format=auto&w=1920&q=75"
      }
    ],
    [
      {
        text: "It was great working with Websigma. Their approach of understanding our requirements and being supportive during our association was a good experience. I wish them the best.",
        name: "Dinesh Kotha",
        position: "CEO,Confirmtkt",
        image: "https://images.yourstory.com/cs/images/people/C7BNKT6W400x400-1596722008878.jpg?format=auto&w=1920&q=75"
      },
      {
        text: "I've worked with many web development companies, but Websigma stands out for their technical expertise, creativity, and responsive customer service. Highly recommended!",
        name: "Anita Desai",
        position: "CTO, InnovateX",
        image: "https://randomuser.me/api/portraits/women/67.jpg"
      }
    ],
    [
      {
        text: "The SEO optimization services provided by Websigma helped us achieve top rankings for our key search terms. Our organic traffic has doubled in just three months!",
        name: "Vikram Singh",
        position: "Digital Manager, GrowthLabs",
        image: "https://randomuser.me/api/portraits/men/26.jpg"
      },
      {
        text: "Websigma delivered our project on time and within budget. Their team was professional, communicative, and truly cared about our success. We'll definitely work with them again.",
        name: "Meera Reddy",
        position: "Operations Manager, BuildRight Solutions",
        image: "https://randomuser.me/api/portraits/women/53.jpg"
      }
    ]
  ];
  
  const totalSlides = testimonials.length;
  
  useEffect(() => {
    let interval;
    if (autoSlide) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoSlide, totalSlides]);
  
  const nextSlide = () => {
    setAutoSlide(false);
    setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };
  
  const prevSlide = () => {
    setAutoSlide(false);
    setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };
  
  const goToSlide = (index) => {
    setAutoSlide(false);
    setCurrentSlide(index);
  };

  return (
    <TestimonialsSection id="testimonials">
      <TestimonialsContainer>
        <SectionHeader
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <SectionSubtitle variants={fadeInUp}>Testimonials</SectionSubtitle>
          <SectionTitle variants={fadeInUp}>
            What Our <span>Clients Say</span>
          </SectionTitle>
          <SectionDescription variants={fadeInUp}>
            We take pride in delivering exceptional results for our clients. Here's what some of them have to say about working with us.
          </SectionDescription>
        </SectionHeader>
        
        <TestimonialsWrapper>
          <TestimonialSlider currentSlide={currentSlide}>
            {testimonials.map((slide, slideIndex) => (
              <TestimonialSlide key={slideIndex}>
                {slide.map((testimonial, index) => (
                  <TestimonialCard
                    key={index}
                    as={motion.div}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={{
                      hidden: { opacity: 0, y: 30 },
                      visible: { 
                        opacity: 1, 
                        y: 0,
                        transition: { duration: 0.5, delay: index * 0.2 }
                      }
                    }}
                  >
                    <TestimonialContent>
                      <TestimonialText>
                        {testimonial.text}
                      </TestimonialText>
                      <TestimonialClient>
                        <ClientImage>
                          <img src={testimonial.image} alt={testimonial.name} />
                        </ClientImage>
                        <ClientInfo>
                          <h4>{testimonial.name}</h4>
                          <p>{testimonial.position}</p>
                        </ClientInfo>
                      </TestimonialClient>
                    </TestimonialContent>
                  </TestimonialCard>
                ))}
              </TestimonialSlide>
            ))}
          </TestimonialSlider>
          
          <TestimonialControls>
            <ControlButton 
              onClick={prevSlide}
              aria-label="Previous testimonial"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
              </svg>
            </ControlButton>
            <ControlButton 
              onClick={nextSlide}
              aria-label="Next testimonial"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
              </svg>
            </ControlButton>
          </TestimonialControls>
          
          <TestimonialDots>
            {testimonials.map((_, index) => (
              <Dot 
                key={index} 
                $active={currentSlide === index} 
                onClick={() => goToSlide(index)}
                aria-label={`Go to testimonial slide ${index + 1}`}
              />
            ))}
          </TestimonialDots>
        </TestimonialsWrapper>
      </TestimonialsContainer>
    </TestimonialsSection>
  );
};

export default Testimonials;
